import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Badge, Button, Col, Row } from 'react-bootstrap';
import { connect } from "react-redux";
import { branch, compose, lifecycle, renderComponent, withProps } from "recompose";
import { bindActionCreators } from 'redux';
import { fullName } from '../../../../../services';
import { businessActions, businessTeamsSelector, teamMembersSelector } from '../../../../../state/modules/business';
import { noCapMap, pluralise } from '../../../../../utils';
import { Icon, SortableTable, TeamAvatar, UserAvatar } from '../../../../components';
import Loading from '../../../loading';
import ContentTile from '../../partials/content-tile';
import SearchTile from '../../partials/search-tile';
import TaskBox from './task-box';

const teamColumns = [
  {
    name: 'name',
    display: () => 'Name',
    className: () => 'name'
  },
  {
    name: 'members',
    display: () => 'Team members',
    className: () => 'members'
  }
]

const individualColumns = [
  {
    name: 'name',
    display: () => 'Name',
    className: () => 'name'
  },
  {
    name: 'team',
    display: () => 'Team',
    className: () => 'team'
  }
]

const SelectedContents = ({ task, stepTo }) => {
  const totalContents = task.searchTiles.value.length + task.businessLibraryContent.value.length

  return (
    <div>
      <Row className='no-gutter selected-content-header'>
        <Col xs={12}>
          <h2 className='name'>
            Task content
            <Badge>{`${totalContents} content`}</Badge>
            <Button bsStyle='plain' className='edit-content right' onClick={() => stepTo(1)}><Icon icon='edit-medium' />Edit content</Button>
          </h2>
        </Col>
      </Row>
      <Row id='contents' className='card-gutter row-slider'>
        {
          noCapMap((content, index) =>
            <Col key={`content-${index}`} xs={12} sm={4} md={3} className='col-xl-2-4 bottom-gap'>
              <SearchTile {...content} completionStatus='Unknown' openInNewWindow={true} searchMode={true} />
            </Col>
          )(task.searchTiles.value)
        }
        {
          noCapMap((content, index) =>
            <Col key={`non-cpd-${index}`} xs={12} sm={4} md={3} className='col-xl-2-4 bottom-gap'>
              <ContentTile {...content} completionStatus='Unknown' openInNewWindow={true} searchMode={true} />
            </Col>
          )(task.businessLibraryContent.value)
        }
      </Row>
    </div>
  )
}

const SelectedTeams = compose(
)(({ teamRows, task, stepTo }) => teamRows.length > 0 ? (<div>
  <Row className='no-gutter selected-teams-header'>
    <Col xs={12}>
      <h2 className='teams'>
        Teams assigned to
        <Badge>{`${task.teams.value.length} ${pluralise('team', task.teams.value.length)}`}</Badge>
        <Button bsStyle='plain' className='edit-team-and-individual right' onClick={() => stepTo(2)}><Icon icon='edit-medium' />Edit teams and individuals</Button>
      </h2>
    </Col>
  </Row>
  <SortableTable columns={teamColumns} rows={teamRows} />
</div>) : null)

const SelectedIndividuals = compose(
)(({ individualRows, task, stepTo }) => (<div>
  <Row className='no-gutter selected-individuals-header'>
    <Col xs={12}>
      <h2 className='individuals'>
        Individuals assigned to
        <Badge>{`${task.users.value.length} ${pluralise('member', task.users.value.length)}`}</Badge>
        {
          task.teams.value.length > 0 ? null :
            <Button bsStyle='plain' className='edit-team-and-individual right' onClick={() => stepTo(2)}><Icon icon='edit-medium' />Edit teams and individuals</Button>
        }
      </h2>
    </Col>
  </Row>
  <SortableTable columns={individualColumns} rows={individualRows} />
</div>))

const Review = ({ task, stepTo, teamRows, individualRows }) => (
  <div id='review'>
    <TaskBox task={task} stepTo={stepTo} />
    <SelectedContents task={task} stepTo={stepTo} />
    <hr />
    <SelectedTeams task={task} stepTo={stepTo} teamRows={teamRows} />
    <SelectedIndividuals task={task} stepTo={stepTo} individualRows={individualRows} />
  </div>
)

const mapStateToProps = (state) => ({
  members: teamMembersSelector(state),
  teams: businessTeamsSelector(state),
  isFetchingTeamMembers: state.business.isFetchingTeamMembers
})

const mapDispatchToProps = (dispatch) => ({
  getTeams: bindActionCreators(businessActions.business.team.getTeams, dispatch),
  getTeamMembers: bindActionCreators(businessActions.business.queryTeamMembers, dispatch)
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle({
    componentDidMount() {
      const {
        business,
        getTeams,
        getTeamMembers,
        task
      } = this.props;

      getTeamMembers(business.id, { selectedIds: task.users.value.join(','), selectedOnly: true })

      if (task.teams.value.length > 0) {
        getTeams(business.id, { selectedIds: task.teams.value.join(',') })
      }
    }
  }),
  branch(
    ({ members, isFetchingTeamMembers }) => !members || isFetchingTeamMembers,
    renderComponent(Loading)
  ),
  withProps(
    ({ task, members }) => ({
      individualRows: fp.map(
        (userId) => {
          const user = fp.find(
            ({ user }) => user.id === userId
          )(members)
          const userTeams = fp.flow(
            fp.find(({ user }) => user.id === userId),
            fp.get('teamNames'),
            fp.join(',')
          )(members)
          const userName = user ? fullName(user.user) : ''
          return {
            name: <div>
              <UserAvatar userName={userName} avatar={user ? user.user.avatar : null} />
              <span>{userName}</span>
            </div>,
            team: he.decode(userTeams)
          }
        }
      )(task.users.value)
    })
  ),
  withProps(
    ({ task, teams }) => {
      if (teams !== undefined) {
        return {
          teamRows: fp.map(
            (teamId) => {
              const team = fp.find(
                ({ team }) => team.id === teamId
              )(teams.businessTeams)
              const teamName = he.decode(team.team.name)
              return {
                name: <div>
                  <TeamAvatar teamName={teamName} />
                  <span>{teamName}</span>
                </div>,
                members: team.members.length
              }
            }
          )(task.teams.value)
        }
      } else {
      return {
        teamRows: []
      }
    }
  })
)(Review)


// WEBPACK FOOTER //
// ./src/containers/pages/app/tasks/partials/review.js